import { Color, Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Accordion, { TitleContainer } from 'dashboard/components/Accordion';
import { Contents } from 'dashboard/components-lib/Accordion';
import { Paragraph } from 'design-system/components/Text';

const AccordtionTitleContainer = styled(Paragraph).attrs({ size: 'md' })`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-weight: 500;
  color: ${({ theme }) => theme.color.text.primary};
`;

const AccordionIconContainer = styled.div`
  margin-right: ${Spacing[2]};
`;

const AccordionTitleRootContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  & + svg {
    margin-right: ${Spacing[4]};
  }
`;

type SectionAccordionCondensedProps = {
  title: ReactNode;
  children: ReactNode;
  fullWidth?: boolean;
  defaultExpanded?: boolean;
  ariaLabel?: string;
  icon?: React.ReactNode;
};

const StyledAccordion = styled.div<Partial<Pick<SectionAccordionCondensedProps, 'fullWidth'>>>`
  border-bottom: 1px solid ${Color.CoreSlate100};

  ${TitleContainer} {
    column-gap: ${Spacing[2]};
    padding: ${Spacing[3]} ${Spacing[5]} ${Spacing[3]} ${Spacing[4]};
  }

  ${({ fullWidth }) => {
    return (
      fullWidth &&
      css`
        ${Contents} {
          padding: 0;
        }
      `
    );
  }}

  &:hover > div > ${TitleContainer} {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
`;

export const AccordionTitle = ({
  title,
  ariaLabel,
  icon,
}: Pick<SectionAccordionCondensedProps, 'title' | 'ariaLabel' | 'icon'>) => {
  return (
    <AccordionTitleRootContainer aria-label={ariaLabel}>
      <AccordtionTitleContainer>{title}</AccordtionTitleContainer>
      {icon && <AccordionIconContainer>{icon}</AccordionIconContainer>}
    </AccordionTitleRootContainer>
  );
};

const AccordionChildren = styled.div<Partial<Pick<SectionAccordionCondensedProps, 'fullWidth'>>>`
  overflow: auto;
  flex: 1 0 0;
  min-height: 0;
  max-height: 575px;
  z-index: 0;
  ${({ fullWidth }) =>
    !fullWidth
      ? css`
          padding: ${Spacing[2]} ${Spacing[5]} ${Spacing[5]} ${Spacing[5]};
        `
      : css`
          padding: ${Spacing[2]} 0 ${Spacing[4]};
        `}
`;

const SectionAccordionCondensed = ({
  title,
  children,
  fullWidth,
  defaultExpanded = true,
  ariaLabel,
  icon,
}: SectionAccordionCondensedProps) => {
  return (
    <StyledAccordion fullWidth={fullWidth}>
      <Accordion
        title={<AccordionTitle title={title} ariaLabel={ariaLabel} icon={icon} />}
        defaultExpanded={defaultExpanded}
      >
        <AccordionChildren fullWidth={fullWidth}>{children}</AccordionChildren>
      </Accordion>
    </StyledAccordion>
  );
};

export default SectionAccordionCondensed;
